import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { format, parseISO } from 'date-fns'
import { CalendarDays, CheckCircle2, ChevronRight, XCircle } from 'lucide-react'
import SectionCard from '@/components/dashboard/SectionCard'
import { EmptyState } from '@/components/dashboard/BarListWidget'
import { getInbox, reviewInboxItem, type InboxItem, type RequestCategory } from '@/api/requests'
import { CATEGORY_META, money, typeMeta } from '@/components/requests/requestMeta'

type Category = 'all' | RequestCategory

/** Times are shown in the requester's own timezone (that's what they entered). */
function timeIn(iso: string | null | undefined, tz?: string | null) {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', timeZone: tz || undefined })
  } catch {
    return format(new Date(iso), 'h:mm a')
  }
}

/** Approver inbox: every pending request the signed-in user may decide, across
 *  attendance, finance and other kinds. Leave keeps its own Manage Leaves queue. */
export default function Approvals() {
  const { orgSlug } = useParams<{ orgSlug: string }>()
  const { data, isLoading } = useQuery({ queryKey: ['inbox'], queryFn: getInbox, refetchInterval: 60_000 })
  const [category, setCategory] = useState<Category>('all')
  const items = data?.items || []
  const shown = items.filter((i) => category === 'all' || i.category === category)
  const cats: Category[] = ['all', 'attendance', 'finance', 'other']

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><CheckCircle2 className="h-6 w-6 text-brand" /> Approvals</h1>
        <p className="text-sm text-gray-500">Requests from your team waiting for your decision.</p>
      </div>

      {!!data?.leave_pending && (
        <Link to={`/${orgSlug}/admin/manage-leaves`}
          className="flex items-center gap-3 rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-800 hover:bg-blue-100/70">
          <CalendarDays className="h-5 w-5" />
          <span className="flex-1"><strong>{data.leave_pending}</strong> leave request{data.leave_pending === 1 ? '' : 's'} waiting for you in Manage Leaves</span>
          <ChevronRight className="h-4 w-4" />
        </Link>
      )}

      <SectionCard
        title={`Pending (${items.length})`}
        action={
          <div className="flex gap-1 flex-wrap">
            {cats.map((c) => {
              const n = items.filter((i) => c === 'all' || i.category === c).length
              return (
                <button key={c} onClick={() => setCategory(c)}
                  className={`px-2.5 py-1 rounded-full text-xs ${category === c ? 'bg-gray-900 text-white' : 'text-gray-500 hover:bg-gray-100'}`}>
                  {CATEGORY_META[c].label} {n > 0 && <span className="opacity-70">({n})</span>}
                </button>
              )
            })}
          </div>
        }
        bodyClassName="p-0"
      >
        {isLoading ? <p className="p-6 text-sm text-gray-400">Loading…</p>
          : shown.length === 0 ? <EmptyState label="Nothing waiting for you" />
          : <div className="divide-y divide-gray-100">{shown.map((i) => <InboxRow key={`${i.source}-${i.id}`} item={i} />)}</div>}
      </SectionCard>
    </div>
  )
}

function InboxRow({ item }: { item: InboxItem }) {
  const qc = useQueryClient()
  const [note, setNote] = useState('')
  const [error, setError] = useState('')
  const mut = useMutation({
    mutationFn: (status: 'approved' | 'rejected') => reviewInboxItem(item, status, note),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inbox'] })
      qc.invalidateQueries({ queryKey: ['inbox-summary'] })
      qc.invalidateQueries({ queryKey: ['attendance-approvals'] })
    },
    onError: (e: any) => setError(e?.response?.data?.detail || 'Could not update the request'),
  })
  const { icon: Icon, tint } = typeMeta(item.source === 'timed' ? 'timed' : item.kind)
  const day = item.start_date ? format(parseISO(item.start_date), 'EEE, d MMM yyyy') : null
  const range = day && item.end_date && item.end_date !== item.start_date ? `${day} → ${format(parseISO(item.end_date), 'EEE, d MMM yyyy')}` : day

  const facts: (string | null | undefined)[] =
    item.source === 'regularization' ? [day, `In ${timeIn(item.checkin, item.user_timezone)}`, `Out ${timeIn(item.checkout, item.user_timezone)}`]
    : item.source === 'timed' ? [day, timeIn(item.checkin, item.user_timezone), item.wfh ? 'WFH' : null]
    : item.source === 'overtime' ? [day, `${item.hours}h extra`]
    : [item.title, item.amount != null ? money(item.amount) : null,
       item.kind === 'loan' && item.installments ? `${item.installments} instalments (${money(Math.round((item.amount || 0) / item.installments))}/month)` : null,
       range]

  const finalStep = item.source !== 'request' || item.final_on_approve !== false

  return (
    <div className="p-4 flex flex-col sm:flex-row gap-4">
      <div className="flex items-start gap-3 flex-1 min-w-0">
        <div className={`h-10 w-10 rounded-lg flex items-center justify-center shrink-0 ${tint}`}><Icon className="h-5 w-5" /></div>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-gray-900">{item.user_name}</span>
            <span className="text-sm text-gray-500">· {item.kind_label}</span>
            {item.source === 'request' && item.category === 'finance' && (
              <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-600">
                {item.stage === 'hr' ? 'Final approval' : 'Manager step'}
              </span>
            )}
          </div>
          <p className="text-sm text-gray-600 mt-0.5">{facts.filter(Boolean).join(' · ')}</p>
          {item.details && <p className="text-xs text-gray-500 mt-1 whitespace-pre-line line-clamp-3">{item.details}</p>}
          {item.attachment_url && <a href={item.attachment_url} target="_blank" rel="noopener noreferrer" className="text-xs text-brand hover:underline">View receipt</a>}
          {item.manager_reviewer_name && (
            <p className="text-xs text-green-700 mt-1">Approved by manager <strong>{item.manager_reviewer_name}</strong>{item.manager_note ? ` — ${item.manager_note}` : ''}</p>
          )}
          <p className="text-[11px] text-gray-400 mt-1">Requested {format(new Date(item.created_at), 'd MMM, h:mm a')}</p>
        </div>
      </div>
      <div className="sm:w-64 shrink-0 space-y-2">
        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional)"
          className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30" />
        <div className="flex gap-2">
          <button disabled={mut.isPending} onClick={() => mut.mutate('approved')}
            className="flex-1 inline-flex items-center justify-center gap-1 rounded-lg bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-60">
            <CheckCircle2 className="h-4 w-4" /> {finalStep ? 'Approve' : 'Approve & forward'}
          </button>
          <button disabled={mut.isPending} onClick={() => mut.mutate('rejected')}
            className="flex-1 inline-flex items-center justify-center gap-1 rounded-lg border border-red-200 px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-60">
            <XCircle className="h-4 w-4" /> Reject
          </button>
        </div>
        {error && <p className="text-xs text-red-600">{error}</p>}
      </div>
    </div>
  )
}
